/**
 * Episode Form View (unified create + edit)
 *
 * Single component handling both create and edit modes:
 * - React Hook Form for state management
 * - Zod validation matching backend schemas
 * - Drag-and-drop reordering for goals and personas
 * - Multi-section form layout
 * - Visual assets (thumbnailUrl, backgroundImageUrl) and persona thumbnails
 * - Contextual dictionary with localized content
 * - LLM-Assisted Refinement Panel (edit mode only)
 * - Clone as new Draft button (edit mode only)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useEpisode,
  useCreateEpisode,
  useUpdateEpisode,
  type EpisodeCreate,
  type EpisodeUpdate,
} from '../services/episodesApi';
import { RefinementPanel } from '../components/RefinementPanel';
import { type GeneratedEpisode } from '../services/generationApi';
import { useInitialData, useTagsFromInitialData } from 'contexts/InitialDataContext';
import { useLanguage } from 'contexts/LanguageContext';
import { getSkillLevelEnglishName } from 'services/initialDataApi';
import { getApiErrorMessage } from 'utils/apiErrors';

// ============================================================================
// Zod Validation Schema (matching backend)
// ============================================================================

const PersonaSchema = z.object({
  id: z.string(), // For drag-and-drop tracking
  name: z.string().min(1, 'Persona name is required').max(100, 'Persona name too long'),
  role: z.string().min(1, 'Persona role is required').max(100, 'Persona role too long'),
  description: z
    .string()
    .min(1, 'Persona description is required')
    .max(500, 'Persona description too long'),
  // Story 3.11: Persona thumbnail URL
  thumbnailUrl: z.string().url('Invalid URL format').nullable().optional().or(z.literal('')),
});

// Story 3.11: Dictionary entry schema for contextual vocabulary
const LocalizedDictionaryContentSchema = z.object({
  explanation: z.string().min(1),
  examplePhrases: z.array(z.string().min(1)).min(1).max(3),
});

const DictionaryEntrySchema = z.object({
  id: z.string(), // For tracking in the form
  word: z.string().min(1, 'Word is required'),
  explanation: z.string().min(1, 'Explanation is required'),
  examplePhrases: z
    .array(z.string().min(1, 'Phrase cannot be empty'))
    .min(1, 'At least 1 example phrase is required')
    .max(3, 'Maximum 3 example phrases allowed'),
  localized: z.record(z.string(), LocalizedDictionaryContentSchema).nullable().optional(),
});

const EpisodeFormSchema = z.object({
  name: z.string().min(1, 'Episode name is required').max(200, 'Name too long (max 200 chars)'),
  languageCode: z.string().min(1, 'Language is required'),
  durationId: z.number().int().min(1).max(4, 'Duration ID must be 1-4'), // Story 15.11: Categorical duration
  context: z.string().min(1, 'Context is required'),
  goals: z
    .array(
      z.object({
        id: z.string(), // For drag-and-drop tracking
        text: z.string().min(1, 'Goal cannot be empty'),
      })
    )
    .min(1, 'At least 1 goal is required')
    .max(10, 'Maximum 10 goals allowed'),
  personas: z
    .array(PersonaSchema)
    .min(1, 'At least 1 persona is required')
    .max(5, 'Maximum 5 personas allowed'),
  scenario: z.string().min(1, 'Scenario is required'),
  complexityLevel: z.number().int().min(2).max(5, 'Complexity level must be between 2-5'),
  // Story 15.8: tagIds are now integers. HTML select returns strings, so we transform them.
  tagIds: z.array(z.coerce.number().int()),
  // Story 3.11: Visual assets
  thumbnailUrl: z.string().url('Invalid URL format').nullable().optional().or(z.literal('')),
  backgroundImageUrl: z.string().url('Invalid URL format').nullable().optional().or(z.literal('')),
  // Story 3.11: Dictionary (contextual vocabulary)
  dictionary: z.array(DictionaryEntrySchema).max(10, 'Maximum 10 dictionary entries allowed'),
});

type EpisodeFormData = z.infer<typeof EpisodeFormSchema>;

// ============================================================================
// Image Preview Component (Story 3.11)
// ============================================================================

interface ImagePreviewProps {
  url: string | null | undefined;
  alt: string;
  size?: 'small' | 'medium' | 'large';
  aspectRatio?: 'square' | 'landscape';
}

function ImagePreview({ url, alt, size = 'medium', aspectRatio = 'square' }: ImagePreviewProps) {
  const [hasError, setHasError] = useState(false);

  const squareSizeClasses = {
    small: 'w-16 h-16',
    medium: 'w-32 h-32',
    large: 'w-48 h-48',
  };

  const landscapeMaxWidths = {
    small: 'max-w-[160px]',
    medium: 'max-w-[280px]',
    large: 'max-w-[400px]',
  };

  useEffect(() => {
    setHasError(false);
  }, [url]);

  if (aspectRatio === 'landscape') {
    if (!url || hasError) {
      return (
        <div
          className={`${landscapeMaxWidths[size]} w-full aspect-video bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center justify-center`}
        >
          <div className="text-center text-gray-400 text-xs">
            <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
            {hasError ? 'Load failed' : 'No image'}
          </div>
        </div>
      );
    }

    return (
      <img
        src={url}
        alt={alt}
        className={`${landscapeMaxWidths[size]} w-full h-auto rounded-lg border border-gray-200`}
        onError={() => setHasError(true)}
      />
    );
  }

  // Square aspect ratio
  if (!url || hasError) {
    return (
      <div
        className={`${squareSizeClasses[size]} bg-gray-100 border border-dashed border-gray-300 rounded-lg flex items-center justify-center`}
      >
        <div className="text-center text-gray-400 text-xs">
          <svg className="w-8 h-8 mx-auto mb-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              clipRule="evenodd"
            />
          </svg>
          {hasError ? 'Load failed' : 'No image'}
        </div>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      className={`${squareSizeClasses[size]} object-cover rounded-lg border border-gray-200`}
      onError={() => setHasError(true)}
    />
  );
}

// ============================================================================
// Sortable Item Components
// ============================================================================

interface SortableGoalItemProps {
  id: string;
  index: number;
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
  error?: string;
}

function SortableGoalItem({ id, index, value, onChange, onRemove, error }: SortableGoalItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2 mb-2">
      <button
        type="button"
        className="mt-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
        </svg>
      </button>
      <div className="flex-1">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Goal ${index + 1}`}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
          }`}
          data-testid={`goal-input-${index}`}
        />
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="mt-2 text-red-600 hover:text-red-800"
        data-testid={`remove-goal-${index}`}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}

interface SortablePersonaItemProps {
  id: string;
  index: number;
  name: string;
  role: string;
  description: string;
  thumbnailUrl?: string | null; // Story 3.11
  onNameChange: (value: string) => void;
  onRoleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onThumbnailUrlChange: (value: string) => void; // Story 3.11
  onRemove: () => void;
  nameError?: string;
  roleError?: string;
  descriptionError?: string;
  thumbnailUrlError?: string; // Story 3.11
}

function SortablePersonaItem({
  id,
  index,
  name,
  role,
  description,
  thumbnailUrl,
  onNameChange,
  onRoleChange,
  onDescriptionChange,
  onThumbnailUrlChange,
  onRemove,
  nameError,
  roleError,
  descriptionError,
  thumbnailUrlError,
}: SortablePersonaItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50"
    >
      <div className="flex items-start gap-2 mb-3">
        <button
          type="button"
          className="mt-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" />
          </svg>
        </button>
        <h4 className="flex-1 text-sm font-medium text-gray-700 mt-2">Persona {index + 1}</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
          data-testid={`remove-persona-${index}`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3 ml-7">
        {/* Story 3.11: Persona thumbnail with image preview (hidden for narrator at index 0) */}
        {index > 0 && (
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <ImagePreview url={thumbnailUrl || null} alt={`${name} thumbnail`} size="small" />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Thumbnail URL</label>
              <input
                type="text"
                value={thumbnailUrl || ''}
                onChange={(e) => onThumbnailUrlChange(e.target.value)}
                placeholder="https://example.com/persona-image.jpg"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  thumbnailUrlError
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                data-testid={`persona-thumbnail-url-${index}`}
              />
              {thumbnailUrlError && (
                <p className="text-red-500 text-sm mt-1">{thumbnailUrlError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Character avatar image</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Mr. Clark"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                nameError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              data-testid={`persona-name-${index}`}
            />
            {nameError && <p className="text-red-500 text-sm mt-1">{nameError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <input
              type="text"
              value={role}
              onChange={(e) => onRoleChange(e.target.value)}
              placeholder="e.g. Landlord"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                roleError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              data-testid={`persona-role-${index}`}
            />
            {roleError && <p className="text-red-500 text-sm mt-1">{roleError}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Persona description"
            rows={3}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
              descriptionError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            data-testid={`persona-description-${index}`}
          />
          {descriptionError && <p className="text-red-500 text-sm mt-1">{descriptionError}</p>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Dictionary Entry Item Component (Story 3.11)
// ============================================================================

interface DictionaryEntryItemProps {
  index: number;
  word: string;
  explanation: string;
  examplePhrases: string[];
  localized?: Record<string, { explanation: string; examplePhrases: string[] }> | null;
  onWordChange: (value: string) => void;
  onExplanationChange: (value: string) => void;
  onPhraseChange: (phraseIndex: number, value: string) => void;
  onAddPhrase: () => void;
  onRemovePhrase: (phraseIndex: number) => void;
  onRemove: () => void;
  wordError?: string;
  explanationError?: string;
  phrasesError?: string;
  phraseErrors?: string[];
}

function DictionaryEntryItem({
  index,
  word,
  explanation,
  examplePhrases,
  localized,
  onWordChange,
  onExplanationChange,
  onPhraseChange,
  onAddPhrase,
  onRemovePhrase,
  onRemove,
  wordError,
  explanationError,
  phrasesError,
  phraseErrors,
}: DictionaryEntryItemProps) {
  const [showLocalized, setShowLocalized] = useState(false);
  const hasLocalizations = localized && Object.keys(localized).length > 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
      <div className="flex items-start justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700">Entry {index + 1}</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
          data-testid={`remove-dictionary-${index}`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {/* Word */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Word <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={word}
            onChange={(e) => onWordChange(e.target.value)}
            placeholder="Target vocabulary word"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
              wordError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            data-testid={`dictionary-word-${index}`}
          />
          {wordError && <p className="text-red-500 text-sm mt-1">{wordError}</p>}
        </div>

        {/* Explanation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Explanation <span className="text-red-500">*</span>
          </label>
          <textarea
            value={explanation}
            onChange={(e) => onExplanationChange(e.target.value)}
            placeholder="Brief explanation or definition in target language"
            rows={2}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
              explanationError
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            data-testid={`dictionary-explanation-${index}`}
          />
          {explanationError && <p className="text-red-500 text-sm mt-1">{explanationError}</p>}
        </div>

        {/* Example Phrases */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Example Phrases <span className="text-red-500">*</span> (1-3)
            </label>
            <button
              type="button"
              onClick={onAddPhrase}
              disabled={examplePhrases.length >= 3}
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid={`add-phrase-${index}`}
            >
              Add Phrase
            </button>
          </div>

          {examplePhrases.map((phrase, phraseIndex) => (
            <div key={phraseIndex} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={phrase}
                onChange={(e) => onPhraseChange(phraseIndex, e.target.value)}
                placeholder={`Example phrase ${phraseIndex + 1}`}
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  phraseErrors?.[phraseIndex]
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                data-testid={`dictionary-phrase-${index}-${phraseIndex}`}
              />
              <button
                type="button"
                onClick={() => onRemovePhrase(phraseIndex)}
                disabled={examplePhrases.length <= 1}
                className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                data-testid={`remove-phrase-${index}-${phraseIndex}`}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ))}
          {phrasesError && <p className="text-red-500 text-sm mt-1">{phrasesError}</p>}
        </div>

        {/* Localized content (read-only, expandable) */}
        {hasLocalizations && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowLocalized(!showLocalized)}
              className="flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <svg
                className={`w-4 h-4 mr-1 transition-transform ${showLocalized ? 'rotate-90' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Translations ({Object.keys(localized).length})
            </button>

            {showLocalized && (
              <div className="mt-2 pl-4 space-y-3">
                {Object.entries(localized).map(([langCode, content]) => (
                  <div key={langCode} className="bg-white p-3 rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      {langCode}
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Explanation:</span>{' '}
                        <span className="text-gray-900">{content.explanation}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Phrases:</span>
                        <ul className="list-disc list-inside ml-2">
                          {content.examplePhrases.map((phrase, i) => (
                            <li key={i} className="text-gray-900">
                              {phrase}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function EpisodeFormView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: episodeId } = useParams<{ id: string }>();
  const isEdit = Boolean(episodeId);

  const [formError, setFormError] = useState<string | null>(null);
  const [changesMade, setChangesMade] = useState<string[]>([]);
  // Get recommendations from navigation state (from judge evaluation) — edit mode only
  const initialRecommendations = (location.state as { recommendations?: string })?.recommendations;

  const { state: initialDataState } = useInitialData();
  const { selectedLanguage } = useLanguage();
  const { data: episode, isLoading: isLoadingEpisode } = useEpisode(isEdit ? episodeId : undefined);
  const tags = useTagsFromInitialData();
  const createEpisodeMutation = useCreateEpisode();
  const updateEpisodeMutation = useUpdateEpisode();

  // Get skill levels and durations from initial data
  const skillLevels = initialDataState.data?.skillLevels || [];
  const episodeDurations = initialDataState.data?.episodeDurations || [];

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EpisodeFormData>({
    resolver: zodResolver(EpisodeFormSchema),
    defaultValues: {
      name: '',
      languageCode: selectedLanguage,
      durationId: 2, // Default to Medium duration (Story 15.11)
      context: '',
      goals: [{ id: crypto.randomUUID(), text: '' }],
      personas: [
        { id: crypto.randomUUID(), name: '', role: '', description: '', thumbnailUrl: '' },
      ],
      scenario: '',
      complexityLevel: 3, // Default to Intermediate
      tagIds: [],
      thumbnailUrl: '',
      backgroundImageUrl: '',
      dictionary: [],
    },
  });

  const {
    fields: goalFields,
    append: appendGoal,
    remove: removeGoal,
    update: updateGoal,
    move: moveGoal,
  } = useFieldArray({
    control,
    name: 'goals',
  });

  const {
    fields: personaFields,
    append: appendPersona,
    remove: removePersona,
    move: movePersona,
  } = useFieldArray({
    control,
    name: 'personas',
  });

  // Story 3.11: Dictionary field array
  const {
    fields: dictionaryFields,
    append: appendDictionary,
    remove: removeDictionary,
    update: updateDictionary,
  } = useFieldArray({
    control,
    name: 'dictionary',
  });

  const complexityLevel = watch('complexityLevel');
  const watchedPersonas = watch('personas');

  // Load episode data on mount (edit mode only)
  useEffect(() => {
    if (episode) {
      setValue('name', episode.name);
      setValue('languageCode', episode.languageCode);
      setValue('durationId', episode.duration.id);
      setValue('context', episode.context);
      setValue(
        'goals',
        episode.goals.map((text) => ({ id: crypto.randomUUID(), text }))
      );
      setValue(
        'personas',
        episode.personas.map((p) => ({
          id: crypto.randomUUID(),
          name: p.name,
          role: p.role || p.name,
          description: p.description,
          thumbnailUrl: p.thumbnailUrl || '',
        }))
      );
      setValue('scenario', episode.scenario);
      setValue('complexityLevel', episode.complexityLevel);
      setValue('tagIds', episode.tagIds);
      setValue('thumbnailUrl', episode.thumbnailUrl || '');
      setValue('backgroundImageUrl', episode.backgroundImageUrl || '');
      if (episode.dictionary) {
        setValue(
          'dictionary',
          episode.dictionary.map((entry) => ({
            id: crypto.randomUUID(),
            word: entry.word,
            explanation: entry.explanation,
            examplePhrases: entry.examplePhrases,
            localized: entry.localized,
          }))
        );
      }
    }
  }, [episode, setValue]);

  // Sync language from global context (create mode only)
  useEffect(() => {
    if (!isEdit) {
      setValue('languageCode', selectedLanguage);
    }
  }, [isEdit, selectedLanguage, setValue]);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleGoalDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = goalFields.findIndex((field) => field.id === active.id);
      const newIndex = goalFields.findIndex((field) => field.id === over.id);
      moveGoal(oldIndex, newIndex);
    }
  };

  const handlePersonaDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = personaFields.findIndex((field) => field.id === active.id);
      const newIndex = personaFields.findIndex((field) => field.id === over.id);
      movePersona(oldIndex, newIndex);
    }
  };

  const handleComplexityChange = (level: number) => {
    setValue('complexityLevel', level);
  };

  // Handle refinement panel callback (edit mode only)
  const handleEpisodeRefined = (refinedEpisode: GeneratedEpisode, changes: string[]) => {
    setChangesMade(changes);

    setValue('name', refinedEpisode.name);
    setValue('context', refinedEpisode.context);
    setValue(
      'goals',
      refinedEpisode.goals.map((text) => ({ id: crypto.randomUUID(), text }))
    );
    setValue(
      'personas',
      refinedEpisode.personas.map((p) => ({ id: crypto.randomUUID(), ...p }))
    );
    setValue('scenario', refinedEpisode.scenario);

    if (refinedEpisode.durationId) {
      setValue('durationId', refinedEpisode.durationId);
    }
    if (refinedEpisode.complexityLevel) {
      setValue('complexityLevel', refinedEpisode.complexityLevel);
    }
    if (refinedEpisode.tagIds) {
      setValue('tagIds', refinedEpisode.tagIds);
    }
  };

  // Build common payload fields shared by create, update, and clone
  const buildPayloadBase = (data: EpisodeFormData) => ({
    name: data.name,
    durationId: data.durationId,
    context: data.context,
    goals: data.goals.map((g) => g.text),
    personas: data.personas.map((p) => ({
      name: p.name,
      role: p.role,
      description: p.description,
      thumbnailUrl: p.thumbnailUrl || null,
    })),
    scenario: data.scenario,
    complexityLevel: data.complexityLevel,
    tagIds: data.tagIds,
    thumbnailUrl: data.thumbnailUrl || null,
    backgroundImageUrl: data.backgroundImageUrl || null,
    dictionary:
      data.dictionary.length > 0
        ? data.dictionary.map((entry) => ({
            word: entry.word,
            explanation: entry.explanation,
            examplePhrases: entry.examplePhrases,
            localized: entry.localized,
          }))
        : null,
  });

  const onSubmit = async (data: EpisodeFormData) => {
    setFormError(null);

    try {
      if (isEdit && episodeId) {
        const payload: EpisodeUpdate = buildPayloadBase(data);
        await updateEpisodeMutation.mutateAsync({ episodeId, update: payload });
        toast.success('Episode updated successfully!');
      } else {
        const payload: EpisodeCreate = {
          ...buildPayloadBase(data),
          languageCode: data.languageCode,
        };
        await createEpisodeMutation.mutateAsync(payload);
        toast.success('Episode created as draft!');
      }
      navigate('/episodes');
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setFormError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Clone as new Draft handler (edit mode only)
  const handleSaveAsNewDraft = async () => {
    setFormError(null);

    try {
      const data = watch();
      const payload: EpisodeCreate = {
        ...buildPayloadBase(data),
        name: `${data.name} (Copy)`,
        languageCode: data.languageCode,
      };

      await createEpisodeMutation.mutateAsync(payload);
      toast.success('New draft created successfully!');
      navigate('/episodes');
    } catch (err) {
      const errorMessage = getApiErrorMessage(err);
      setFormError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Edit mode: loading state
  if (isEdit && isLoadingEpisode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
          role="status"
          aria-label="Loading episode"
        />
      </div>
    );
  }

  // Edit mode: episode not found
  if (isEdit && !episode) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Episode</h1>
          <Link to="/episodes" className="text-blue-600 hover:text-blue-800 font-medium">
            ← Back to Episodes
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          Episode not found
        </div>
      </div>
    );
  }

  // Convert current form data to GeneratedEpisode format for refinement panel (edit mode)
  const currentEpisodeForRefinement: GeneratedEpisode = {
    name: watch('name'),
    context: watch('context'),
    goals: watch('goals').map((g) => g.text),
    personas: watch('personas').map((p) => ({
      name: p.name,
      role: p.role,
      description: p.description,
    })),
    scenario: watch('scenario'),
    durationId: watch('durationId'),
    complexityLevel: watch('complexityLevel'),
    tagIds: watch('tagIds'),
  };

  const isSaving = isEdit ? updateEpisodeMutation.isPending : createEpisodeMutation.isPending;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Episode' : 'Create New Episode'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEdit
              ? 'Update episode details and configuration'
              : 'Create a new episode with personas, goals, and scenarios'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/episodes" className="text-blue-600 hover:text-blue-800 font-medium">
            ← Back to Episodes
          </Link>
        </div>
      </div>

      {/* Form-level Error */}
      {formError && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6"
          data-testid="form-error"
        >
          {formError}
        </div>
      )}

      {/* Changes Made Display (edit mode only) */}
      {isEdit && changesMade.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Recent Refinement Changes:</h3>
          <ul className="list-disc list-inside space-y-1">
            {changesMade.map((change, index) => (
              <li key={index} className="text-sm text-blue-800">
                {change}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Basic Information</h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Episode Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                {...register('name')}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Enter episode name (max 200 characters)"
                data-testid="name-input"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
            </div>

            {/* Language — static badge from global language context */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <span
                className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                data-testid="language-badge"
              >
                {initialDataState.data?.languages?.find((l) => l.code === watch('languageCode'))
                  ?.name || watch('languageCode').toUpperCase()}
              </span>
            </div>

            {/* Duration - Story 15.11: Categorical duration */}
            <div>
              <label htmlFor="durationId" className="block text-sm font-medium text-gray-700 mb-1">
                Duration <span className="text-red-500">*</span>
              </label>
              <select
                id="durationId"
                {...register('durationId', { valueAsNumber: true })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.durationId
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                data-testid="duration-select"
              >
                {episodeDurations.map((duration) => (
                  <option key={duration.id} value={duration.id}>
                    {duration.localizations['en']?.name || `Duration ${duration.id}`} (≤
                    {duration.max_turns} turns)
                  </option>
                ))}
              </select>
              {errors.durationId && (
                <p className="text-red-500 text-sm mt-1">{errors.durationId.message}</p>
              )}
            </div>

            {/* Complexity Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Complexity Level <span className="text-red-500">*</span>
              </label>
              <select
                value={complexityLevel || ''}
                onChange={(e) => handleComplexityChange(Number(e.target.value))}
                className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="complexity-select"
              >
                <option value="">Select complexity level...</option>
                {skillLevels.map((skillLevel) => (
                  <option key={skillLevel.id} value={skillLevel.id}>
                    {getSkillLevelEnglishName(skillLevel)}
                  </option>
                ))}
              </select>
              {errors.complexityLevel && (
                <p className="text-red-500 text-sm mt-1">{errors.complexityLevel.message}</p>
              )}
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tagIds" className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <Controller
                name="tagIds"
                control={control}
                render={({ field }) => (
                  <select
                    id="tagIds"
                    multiple
                    ref={field.ref}
                    name={field.name}
                    onBlur={field.onBlur}
                    value={field.value?.map(String) ?? []}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, (opt) =>
                        Number(opt.value)
                      );
                      field.onChange(selected);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    size={5}
                    data-testid="tags-select"
                  >
                    {tags.map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              <p className="text-sm text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple tags</p>
            </div>
          </div>
        </div>

        {/* Context & Goals Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Context & Goals</h2>

          <div className="space-y-4">
            {/* Context */}
            <div>
              <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-1">
                Context <span className="text-red-500">*</span>
              </label>
              <textarea
                id="context"
                {...register('context')}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.context
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                placeholder="Provide context for the episode"
                data-testid="context-input"
              />
              {errors.context && (
                <p className="text-red-500 text-sm mt-1">{errors.context.message}</p>
              )}
            </div>

            {/* Goals */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Learning Goals <span className="text-red-500">*</span> (1-10)
                </label>
                <button
                  type="button"
                  onClick={() => appendGoal({ id: crypto.randomUUID(), text: '' })}
                  disabled={goalFields.length >= 10}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  data-testid="add-goal-button"
                >
                  Add Goal
                </button>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleGoalDragEnd}
              >
                <SortableContext items={goalFields} strategy={verticalListSortingStrategy}>
                  {goalFields.map((field, index) => (
                    <SortableGoalItem
                      key={field.id}
                      id={field.id}
                      index={index}
                      value={field.text}
                      onChange={(value) => updateGoal(index, { ...field, text: value })}
                      onRemove={() => removeGoal(index)}
                      error={errors.goals?.[index]?.text?.message}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {errors.goals?.message && (
                <p className="text-red-500 text-sm mt-1">{errors.goals.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Personas Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              Personas <span className="text-red-500">*</span> (1-5)
            </h2>
            <button
              type="button"
              onClick={() =>
                appendPersona({
                  id: crypto.randomUUID(),
                  name: '',
                  role: '',
                  description: '',
                  thumbnailUrl: '',
                })
              }
              disabled={personaFields.length >= 5}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid="add-persona-button"
            >
              Add Persona
            </button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handlePersonaDragEnd}
          >
            <SortableContext items={personaFields} strategy={verticalListSortingStrategy}>
              {personaFields.map((field, index) => {
                const watched = watchedPersonas?.[index];
                return (
                  <SortablePersonaItem
                    key={field.id}
                    id={field.id}
                    index={index}
                    name={watched?.name ?? field.name}
                    role={watched?.role ?? field.role}
                    description={watched?.description ?? field.description}
                    thumbnailUrl={watched?.thumbnailUrl ?? field.thumbnailUrl}
                    onNameChange={(value) => setValue(`personas.${index}.name`, value)}
                    onRoleChange={(value) => setValue(`personas.${index}.role`, value)}
                    onDescriptionChange={(value) =>
                      setValue(`personas.${index}.description`, value)
                    }
                    onThumbnailUrlChange={(value) =>
                      setValue(`personas.${index}.thumbnailUrl`, value)
                    }
                    onRemove={() => removePersona(index)}
                    nameError={errors.personas?.[index]?.name?.message}
                    roleError={errors.personas?.[index]?.role?.message}
                    descriptionError={errors.personas?.[index]?.description?.message}
                    thumbnailUrlError={errors.personas?.[index]?.thumbnailUrl?.message}
                  />
                );
              })}
            </SortableContext>
          </DndContext>

          {errors.personas?.message && (
            <p className="text-red-500 text-sm mt-1">{errors.personas.message}</p>
          )}
        </div>

        {/* Visual Assets Section (Story 3.11) */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Visual Assets</h2>
          <p className="text-sm text-gray-500 mb-4">
            Episode thumbnail and background images for the mobile app.
          </p>

          <div className="space-y-6">
            {/* Thumbnail URL */}
            <div>
              <label
                htmlFor="thumbnailUrl"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Thumbnail URL
              </label>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <ImagePreview url={watch('thumbnailUrl')} alt="Episode thumbnail" size="medium" />
                </div>
                <div className="flex-1">
                  <input
                    id="thumbnailUrl"
                    type="text"
                    {...register('thumbnailUrl')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.thumbnailUrl
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="https://example.com/thumbnail.jpg"
                    data-testid="thumbnail-url-input"
                  />
                  {errors.thumbnailUrl && (
                    <p className="text-red-500 text-sm mt-1">{errors.thumbnailUrl.message}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Episode card image shown in episode list (1:1 aspect ratio recommended)
                  </p>
                </div>
              </div>
            </div>

            {/* Background Image URL */}
            <div>
              <label
                htmlFor="backgroundImageUrl"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Background Image URL
              </label>
              <div className="space-y-3">
                <ImagePreview
                  url={watch('backgroundImageUrl')}
                  alt="Episode background"
                  size="large"
                  aspectRatio="landscape"
                />
                <div>
                  <input
                    id="backgroundImageUrl"
                    type="text"
                    {...register('backgroundImageUrl')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                      errors.backgroundImageUrl
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="https://example.com/background.jpg"
                    data-testid="background-image-url-input"
                  />
                  {errors.backgroundImageUrl && (
                    <p className="text-red-500 text-sm mt-1">{errors.backgroundImageUrl.message}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Scene background during episode playback (16:9 aspect ratio recommended)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dictionary Section (Story 3.11) */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold">Contextual Dictionary</h2>
              <p className="text-sm text-gray-500 mt-1">
                Key vocabulary for this episode (up to 10 entries)
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                appendDictionary({
                  id: crypto.randomUUID(),
                  word: '',
                  explanation: '',
                  examplePhrases: [''],
                  localized: null,
                })
              }
              disabled={dictionaryFields.length >= 10}
              className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              data-testid="add-dictionary-button"
            >
              Add Entry
            </button>
          </div>

          {dictionaryFields.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <svg
                className="w-12 h-12 mx-auto mb-3 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <p>No dictionary entries yet</p>
              <p className="text-sm">Add vocabulary words that learners should focus on</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dictionaryFields.map((field, index) => (
                <DictionaryEntryItem
                  key={field.id}
                  index={index}
                  word={field.word}
                  explanation={field.explanation}
                  examplePhrases={field.examplePhrases}
                  localized={field.localized}
                  onWordChange={(value) => updateDictionary(index, { ...field, word: value })}
                  onExplanationChange={(value) =>
                    updateDictionary(index, { ...field, explanation: value })
                  }
                  onPhraseChange={(phraseIndex, value) => {
                    const newPhrases = [...field.examplePhrases];
                    newPhrases[phraseIndex] = value;
                    updateDictionary(index, { ...field, examplePhrases: newPhrases });
                  }}
                  onAddPhrase={() => {
                    if (field.examplePhrases.length < 3) {
                      updateDictionary(index, {
                        ...field,
                        examplePhrases: [...field.examplePhrases, ''],
                      });
                    }
                  }}
                  onRemovePhrase={(phraseIndex) => {
                    if (field.examplePhrases.length > 1) {
                      const newPhrases = field.examplePhrases.filter((_, i) => i !== phraseIndex);
                      updateDictionary(index, { ...field, examplePhrases: newPhrases });
                    }
                  }}
                  onRemove={() => removeDictionary(index)}
                  wordError={errors.dictionary?.[index]?.word?.message}
                  explanationError={errors.dictionary?.[index]?.explanation?.message}
                  phrasesError={errors.dictionary?.[index]?.examplePhrases?.message}
                  phraseErrors={errors.dictionary?.[index]?.examplePhrases
                    ?.map?.((e) => e?.message)
                    .filter((msg): msg is string => msg !== undefined)}
                />
              ))}
            </div>
          )}

          {errors.dictionary?.message && (
            <p className="text-red-500 text-sm mt-2">{errors.dictionary.message}</p>
          )}
        </div>

        {/* Scenario Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Scenario</h2>

          <div>
            <label htmlFor="scenario" className="block text-sm font-medium text-gray-700 mb-1">
              LLM Scenario Instructions <span className="text-red-500">*</span>
            </label>
            <textarea
              id="scenario"
              {...register('scenario')}
              rows={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.scenario
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Provide instructions for the LLM to generate the scenario"
              data-testid="scenario-input"
            />
            {errors.scenario && (
              <p className="text-red-500 text-sm mt-1">{errors.scenario.message}</p>
            )}
          </div>
        </div>

        {/* LLM-Assisted Refinement Panel (edit mode only) */}
        {isEdit && (
          <RefinementPanel
            episode={currentEpisodeForRefinement}
            onEpisodeRefined={handleEpisodeRefined}
            initialInstructions={initialRecommendations}
          />
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/episodes')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            data-testid="cancel-button"
          >
            Cancel
          </button>

          {/* Clone as new Draft (edit mode only) */}
          {isEdit && (
            <button
              type="button"
              onClick={handleSaveAsNewDraft}
              disabled={createEpisodeMutation.isPending}
              className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400"
              data-testid="clone-as-new-draft-button"
            >
              {createEpisodeMutation.isPending ? 'Cloning...' : 'Clone as new Draft'}
            </button>
          )}

          <button
            type="submit"
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
            data-testid="save-draft-button"
          >
            {isSaving ? 'Saving...' : isEdit ? 'Save' : 'Save as Draft'}
          </button>
        </div>
      </form>
    </div>
  );
}
